// Copyright (c) 2021 WiseTime. All rights reserved.

import React, { useEffect, useState } from "react"
import { Trans } from "@lingui/react"
import { t } from "@lingui/macro"
import { useDispatch, useSelector } from "react-redux"
import styled, { css } from "styled-components/macro"
import { abbreviate } from "lib/utils"
import { AbbreviatedTeamName, ActionPaneButton, IconButtonStyles, WarningIconWrapper } from "features/shared/styles"
import Tooltip from "features/shared/Tooltip"
import * as UserContextSlice from "features/user-context/slice"
import {
  isTimeRequiringTag,
  PostValidationResult,
  validateTimeGroupsForPost,
} from "../timeBlocks"
import { TimeGroup, TimeRow } from "generated/wisetime/user/timeline/v4/timeline_pb"
import * as TimelineSlice from "../slices/timeline"
import * as TimelineActions from "../actions"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import SvgIcon from "features/shared/SvgIcon"
import * as UserCuesSlice from "../../user-cues/slice"
import { buildTagRequiredAppcue } from "../userCues"
import * as UserCuesActions from "../../user-cues/actions"
import { POST_TIME_BUTTON_CLASSNAME, SCROLL_CONTAINER_CLASSNAME } from "../../shared/classNames"
import Dialog from "../../shared/Dialog"
import WarnMultipleTagsPopup from "./WarnMultipleTagsPopup"
import { SitePermission } from "../../../generated/legebill/site/membership/v4/membership_pb"
import { darken } from "polished"
import { showSnackBar, validationSnackBarPayload } from "../../snackbar/actions"
import { hasPermission } from "../../../app/permission"

const Wrapper = styled.div<{ isQuickPost: boolean }>`
  position: relative;
  display: ${({ isQuickPost }) => isQuickPost ? "inline-flex" : "flex"};

  &:hover {
    [data-tooltip] {
      opacity: 1;
      display: inline-block;
    }
  }
`

const SharedDisabledButton = css`
  opacity: .5;
  pointer-events: none;
`

const StyledAbbreviatedTeamName = styled(AbbreviatedTeamName)`
  position: absolute;
  top: 50%;
  left: 8px;
  color: ${({ theme }) => theme.colors.tropicalRainForest};
  border-color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.white};
  margin-top: -10px;
`

const ButtonPost = styled(ActionPaneButton)<{
  isRow?: boolean,
  hasPermission: boolean,
}>`
  position: relative;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.tropicalRainForest};
  transition: background-color .3s ease;
  
  ${({ isRow }) => {
    if (isRow) {
      return IconButtonStyles
    }
    return css`
      min-width: 10.6rem;
      text-align: center;
      padding-right: 36px;
      padding-left: 36px;
    `
  }};
  
  &[disabled] {
    ${SharedDisabledButton};
  }

  ${({ hasPermission, theme }) => {
    if (hasPermission) {
      return css`
        &:hover {
          background-color: ${darken(0.05, theme.colors.tropicalRainForest)};
        }
      `
    }
    return css`
      ${SharedDisabledButton};
    `
  }};
`

const PostIcon = styled(SvgIcon)`
  width: 1.3rem;
  min-width: 1.3rem;
  height: 1.3rem;
`

const StyledWarningIconWrapper = styled(WarningIconWrapper)`
  position: absolute;
  top: -7px;
  left: -7px;
  z-index: 10;
`

type Props = {
  timeGroups?: TimeGroup.AsObject[],
  timeRows?: TimeRow.AsObject[],
  isRow?: boolean,
  hasTagWithDifferentAssignee: boolean,
  createAndPostTimeGroups?: () => void,
}

const PostActions = ({
  timeGroups = [],
  timeRows = [],
  isRow = false,
  hasTagWithDifferentAssignee,
  createAndPostTimeGroups,
}: Props): React.ReactElement<Props> => {
  const dispatch = useDispatch()
  const isPostingTime = useSelector(TimelineAdditionalUiSlice.selectIsPostingTime)
  const postHasDependentProgress = useSelector(TimelineAdditionalUiSlice.selectPostHasDependentProgress)
  const warnMultipleTags = useSelector(TimelineAdditionalUiSlice.selectWarnMultipleTags)
  const updatingDurationTimeGroupIds = useSelector(TimelineAdditionalUiSlice.selectUpdatingDurationTimeGroupIds)
  const teamSitesList = useSelector(UserContextSlice.selectTeamSitesList)
  const currentSiteId = useSelector(UserContextSlice.selectCurrentSiteId)
  const currentSiteRef = useSelector(UserContextSlice.selectCurrentSiteRef)
  const currentSite = useSelector(UserContextSlice.selectCurrentSite)
  const sitePermissions = useSelector(UserContextSlice.selectSitePermissions)
  const hasPostPermission = hasPermission(SitePermission.POST_TIME, sitePermissions)
  const currentSiteTeamName = teamSitesList.find(teamSite => teamSite.id === currentSiteId)?.displayName || ""
  const shownUserCues = useSelector(UserCuesSlice.selectUserCues)
  const [warnMultipleTagsPopupOpened, setWarnMultipleTagsPopupOpened] = useState<boolean>(false)
  const isQuickPost = createAndPostTimeGroups !== undefined
  const isDisabledPostButton = postHasDependentProgress || (!isRow && isPostingTime)
    || (!isQuickPost && timeGroups.some(timeGroup => updatingDurationTimeGroupIds.includes(timeGroup.id)))

  const hasMultipleTags = warnMultipleTags && (isQuickPost ? timeRows : timeGroups)
    .some(item => item.tagsList.filter(t => t.siteId === currentSiteId).length > 1)

  useEffect(() => {
    if (!hasMultipleTags && warnMultipleTagsPopupOpened) {
      setWarnMultipleTagsPopupOpened(false)
    }
  }, [hasMultipleTags, warnMultipleTagsPopupOpened])

  useEffect(() => {
    if (postHasDependentProgress) {
      dispatch(TimelineAdditionalUiSlice.resetPostHasDependentProgress())
      dispatch(TimelineSlice.resetFailedTimeGroupId())
    }
  }, [])

  const showTagIsRequiredAppCue = () => {
    const tagRequiredAppCue = buildTagRequiredAppcue(shownUserCues)
    if (tagRequiredAppCue) {
      const scrollPanel: Element | null | undefined = document.querySelector("." + SCROLL_CONTAINER_CLASSNAME)
      if (scrollPanel) {
        scrollPanel.scrollTop = 0
      }
      dispatch(UserCuesActions.createUserCue(tagRequiredAppCue))
    }
  }

  const handleValidationFailure = (validationResult: PostValidationResult, tagRequired: boolean) => {
    if (tagRequired) {
      showTagIsRequiredAppCue()
    }
    dispatch(TimelineSlice.setTimeGroupsPostErrorMessages(validationResult.errorMessages))
    if (validationResult.snackBarError) {
      dispatch(showSnackBar(validationSnackBarPayload(validationResult.snackBarError)))
    }
  }

  const postTime = (skipWarnMultipleTags = false): void => {
    if (!hasPostPermission || currentSite === undefined) {
      return
    }

    const validationResult = validateTimeGroupsForPost(currentSite, timeGroups)
    const postableTimeGroups = timeGroups.filter(timeGroup => validationResult.postableRowIds.includes(timeGroup.id))
    const postableItems = isQuickPost ? timeRows : postableTimeGroups
    const showWarning = warnMultipleTags
      && !skipWarnMultipleTags
      && postableItems.some(item => item.tagsList.filter(t => t.siteId === currentSiteId).length > 1)

    if (!showWarning && Object.keys(validationResult.errorMessages).length > 0) {
      const tagRequired = timeGroups.some(timeGroup => isTimeRequiringTag(timeGroup, currentSite))
      handleValidationFailure(validationResult, tagRequired)
    }
    if (currentSiteRef === undefined || postableItems.length === 0) {
      return
    }

    if (showWarning) {
      setWarnMultipleTagsPopupOpened(true)
      return
    }

    if (isQuickPost) {
      createAndPostTimeGroups && createAndPostTimeGroups()
      return
    }

    dispatch(TimelineActions.postTime({
      timeGroups: postableTimeGroups,
      currentSiteRef,
      roundingPreference: currentSite.siteRoundingPreference,
    }))
  }

  const handleOk = (dontAskAgain: boolean) => {
    postTime(true)
    setWarnMultipleTagsPopupOpened(false)
    if (dontAskAgain) {
      dispatch(TimelineAdditionalUiSlice.setWarnMultipleTags(false))
    }
  }

  const hideWarnMultipleTags = () => setWarnMultipleTagsPopupOpened(false)
  
  const hasTagWithDifferentAssigneeTitle = () => {
    if (isQuickPost) {
      return t({
        id: "label_some_time_row_not_tag_assignee",
        message: "Please note that one of the time rows contains a tag for which you are not an assignee.",
      })
    }
    if (isRow) {
      return t({
        id: "label_timesheet_not_tag_assignee",
        message: "Timesheet entry contains a tag for which you are not an assignee.",
      })
    }
    return t({
      id: "label_some_timesheet_not_tag_assignee",
      message: "Please note that one of the timesheet entries contains a tag for which you are not an assignee.",
    })
  }

  return (
    <Wrapper isQuickPost={isQuickPost}>
      <ButtonPost
        type="button"
        onClick={(e): void => {
          e.stopPropagation()
          postTime()
        }}
        className={!isRow ? POST_TIME_BUTTON_CLASSNAME : ""}
        tabIndex={isRow ? -1 : 0}
        title={isRow ? t({ id: "label_post", message: "Post" }) : ""}
        hasPermission={hasPostPermission}
        disabled={isDisabledPostButton}
        isRow={isRow}
      >
        {hasTagWithDifferentAssignee && (
          <StyledWarningIconWrapper
            title={hasTagWithDifferentAssigneeTitle()}
          >
            <SvgIcon name="exclamation-circle"/>
          </StyledWarningIconWrapper>
        )}
        {isRow ? (
          <PostIcon name="paper-plane"/>
        ) : (
          <>
            <StyledAbbreviatedTeamName>
              {abbreviate(currentSiteTeamName)}
            </StyledAbbreviatedTeamName>
            {isQuickPost ? <Trans id="label_quick_post" message="Quick Post"/>
              : <Trans id="label_post" message="Post"/>}
          </>
        )}
      </ButtonPost>
      {!hasPostPermission && (
        <Tooltip
          position={isRow ? "top-right" : "top-left"}
          text={(
            <Trans
              id="msg_no_permission_to_post_time"
              message="No permission to post time"
            />
          )}
        />
      )}
      {warnMultipleTagsPopupOpened && (
        <Dialog onClose={hideWarnMultipleTags}>
          <WarnMultipleTagsPopup
            handleOk={handleOk}
            handleCancel={hideWarnMultipleTags}
          />
        </Dialog>
      )}
    </Wrapper>
  )
}

export default PostActions
